import styled from 'styled-components/macro';
import { space, SpaceProps } from 'styled-system';
import { themeGet } from '@styled-system/theme-get';

export const StyledTableWrapper = styled.div`
  position: relative;
  width: 100%;
`;

export const StyledEmpty = styled.div<SpaceProps>`
  margin: 0 auto;

  ${space}
`;

export const StyledItemsEmpty = styled.div`
  padding: 15px 0;
  text-align: center;
  font-weight: ${themeGet('fontWeights.regular')};
  font-size: ${themeGet('fontSizes.base')}px;
`;

export const StyledEmptyText = styled.p`
  text-align: center;
  font-weight: ${themeGet('fontWeights.regular')};
  font-size: ${themeGet('fontSizes.up')}px;
  color: ${themeGet('colors.text.default')};
`;

export const StyledEmptyLink = styled.a`
  color: ${themeGet('colors.text.default')};
  text-decoration: underline;

  &:hover,
  &:focus {
    color: ${themeGet('colors.primary.regular')};
    text-decoration: underline;
  }
`;

export const StyledOverflow = styled.div`
  overflow: scroll;

  &.variant-default {
    max-height: 280px;
  }

  &.variant-certificate {
    max-height: 517px;
  }

  &.variant-offset-activity {
    max-height: 527px;
  }

  &.variant-delegation {
    max-height: 329px;
  }

  .styled-spinner {
    padding: 20px;
  }
`;

export const StyledTable = styled.div`
  overflow: hidden;
  width: 100%;
  border-radius: ${themeGet('radii.base')};
  box-shadow: ${themeGet('shadows.card')};
`;

export const StyledTableCell = styled.div`
  font-weight: ${themeGet('fontWeights.regular')};
  font-size: ${themeGet('fontSizes.sm')}px;
  color: ${themeGet('colors.text.table')};
  line-height: ${themeGet('lineHeights.body')};
  transition: color 0.25s ease;

  &.align-center {
    text-align: center;
  }
`;

export const StyledTableButton = styled.button`
  padding: 0;
  margin: 0;
  border: none;
  background: none;
  color: #5b6075;
  transition: none;

  &.active {
    color: ${themeGet('colors.primary.regular')};

    .dropdown-icon {
      transform: rotate(180deg);
    }
  }
`;

export const StyledTableNested = styled.div`
  padding: 12px;
  margin-top: 10px;
  max-height: 200px;
  overflow: auto;
  border-radius: ${themeGet('radii.base')};
  background-color: #f6f7fa;
`;

export const StyledTableNestedHead = styled.div`
  ${StyledTableCell} {
    font-weight: ${themeGet('fontWeights.semiBold')};
    text-align: center;

    &:first-child {
      text-align: left;
    }
  }

  &:not(:last-child) {
    margin-bottom: 4px;
  }
`;

export const StyledTableNestedRow = styled.div`
  display: grid;
  grid-template-columns:
    minmax(220px, 24%) minmax(140px, 29%) minmax(130px, 27%)
    minmax(180px, 26%);
  grid-gap: 20px;

  &:not(:last-child) {
    margin-bottom: 4px;
  }

  ${StyledTableCell} {
    text-align: center;

    &:first-child {
      text-align: left;
    }
  }
`;

export const StyledTableLine = styled.div<{
  columns?: number;
}>`
  display: grid;
  grid-gap: 30px;
  align-items: center;

  &.variant-default {
    padding: 25px 0;
    grid-template-columns: repeat(
      ${(props) => props.columns || 5},
      minmax(100px, 1fr)
    );
  }

  &.variant-offset-activity {
    padding: 22px 0;
    grid-template-columns:
      minmax(100px, 30%) minmax(100px, 35%) minmax(100px, 18%)
      minmax(100px, 17%);
  }

  &.variant-delegation {
    padding: 22px 0;
    grid-template-columns: repeat(
      ${(props) => props.columns || 5},
      minmax(100px, 1fr)
    );
  }

  &.is--hover {
    cursor: pointer;

    &:hover {
      ${StyledTableCell} {
        color: ${themeGet('colors.primary.regular')};
      }
    }
  }
`;

export const StyledTableRow = styled.div`
  &:not(:last-child) {
    border-bottom-width: 1px;
    border-bottom-style: solid;
    border-bottom-color: ${themeGet('colors.text.border')};
  }

  &.variant-certificate {
    padding: 10px 0;

    ${StyledTableLine} {
      grid-template-columns:
        minmax(45px, 5%) minmax(190px, 24%) minmax(120px, 26%)
        minmax(90px, 24%) minmax(174px, 19%) minmax(20px, 2%);
    }
  }
`;

export const StyledTableHead = styled.div`
  padding: 0 16px;

  &.variant-certificate {
    padding: 0 65px;
  }

  ${StyledTableLine} {
    padding: 0;
  }

  ${StyledTableRow} {
    border-bottom-width: 1px;
    border-bottom-style: solid;
    border-bottom-color: ${themeGet('colors.text.border')};
    padding: 16px 0 10px;
  }

  .table-column-sorters {
    display: flex;
    align-items: center;

    .table-caret {
      line-height: 0;
      color: ${themeGet('colors.primary.extra')};
    }

    &.ascending,
    &.descending {
      .table-caret {
        color: ${themeGet('colors.primary.regular')};
      }
    }

    &.ascending {
      .table-caret {
        transform: rotate(180deg);
      }
    }

    .table-column-sorter {
      display: flex;
      flex-direction: column;
      margin-left: 6px;
      line-height: 1;
    }
  }

  .has-sortable {
    cursor: pointer;
  }

  .table-column-title {
    font-weight: ${themeGet('fontWeights.semi')};
    color: ${themeGet('colors.primary.extra')};
    font-size: ${themeGet('fontSizes.sm')}px;
  }
`;

export const StyledTableBody = styled.div`
  padding: 0 16px;

  &.variant-certificate {
    padding: 0 65px;
  }

  > .styled-spinner {
    padding: 20px;
  }
`;

export const StyledValueWithIcon = styled.div`
  display: flex;
  align-items: center;

  > svg {
    min-width: 16px;
    margin-right: 11px;
  }
`;

export const StyledValueImage = styled.img`
  line-height: 1;
`;

export const StyledCapitalizeText = styled.span`
  text-transform: capitalize;
`;

export const StyledSuccessText = styled.span`
  color: ${themeGet('colors.primary.regular')};
`;

export const StyledErrorText = styled.span`
  color: ${themeGet('colors.input.textError')};
`;

export const StyledLink = styled.a`
  &:hover {
    color: ${themeGet('colors.primary.regular')};
  }
`;

export const StyledLine = styled.div``;
